/* eslint-disable consistent-return */
/* eslint-disable max-len */
import { Spin, Typography } from 'antd';
import { AxiosError } from 'axios';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import CardiovascularRisk from './CardiovascularRisk';

import { useAppSelector } from 'app/store';
import Icon from 'common/components/Icon';
import SummaryCard from 'common/components/SummaryCard';
import {
  getAtherosclerosisV3Service,
  getCounterByTMXVV3Service,
  getCounterHeartRiskHighAndVeryHighV3Service,
  getCounterResultV3Service,
  getYTNCByHeartRiskV3Service,
  getYTNCForOneByHeartRiskV3Service,
  getYTNCV3Service
} from 'common/services/dashboard';
import { HeartRiskEnum } from 'common/services/dashboard/types';
import { defaultDateRangeMonth } from 'common/utils/functions';
import roles, { getPermission } from 'configs/roles';
import HeaderKiosk from 'extends/HeaderKiosk';
import KioskAtherosclerosisChart from 'extends/KioskAtherosclerosisChart';
import { KioskCardStatisticsItem } from 'extends/KioskCardStatistics';
import KioskCardiovascularRiskChart from 'extends/KioskCardiovascularRisk';
import KioskDashboardFilter from 'extends/KioskDashboardFilter';
import KioskHeartRiskClassify from 'extends/KioskHeartRiskClassify';

const RiskHeartManagement = () => {
  const rolesUser = useAppSelector((state) => state.auth.roles);
  const { t } = useTranslation();
  const [typeParam, setTypeParam] = useState<number | undefined>(undefined);
  const [kiosGroupId, setKiosGroupId] = useState<number[]>([]);
  const [dateRange, setDateRange] = useState<string[]>(defaultDateRangeMonth);
  const [isErrorTimeout, setIsErrorTimeout] = useState<boolean>(false);

  const {
    data: ytncData, refetch, isFetching
  } = useQuery(
    ['getYTNCV3Query'],
    () => getYTNCV3Service({
      from: dateRange[0],
      to: dateRange[1],
      ...(typeParam && { type: typeParam }),
      ...(kiosGroupId.join(',') && { kioskGroupIds: kiosGroupId.join(',') }),
    }),
    {
      onError: (err: AxiosError) => {
        if (err.response?.status === 408 || err.response?.status === 504) {
          setIsErrorTimeout(true);
        }
      },
      onSuccess: () => {
        setIsErrorTimeout(false);
      }
    }
  );

  const {
    data: kiosCounterData, refetch: refetchCounter
  } = useQuery(
    ['getCounterResultV3Query3-heart'],
    () => getCounterResultV3Service({
      from: dateRange[0],
      to: dateRange[1],
      ...(typeParam && { type: typeParam }),
      ...(kiosGroupId.join(',') && { kioskGroupIds: kiosGroupId.join(',') }),
    })
  );

  const {
    data: counterHeartRiskData, refetch: refetchHeartRiskCounter
  } = useQuery(
    ['getCounterHeartRiskHighAndVeryHighV3Query3'],
    () => getCounterHeartRiskHighAndVeryHighV3Service({
      from: dateRange[0],
      to: dateRange[1],
      ...(typeParam && { type: typeParam }),
      ...(kiosGroupId.join(',') && { kioskGroupIds: kiosGroupId.join(',') }),
    }),
  );

  const {
    isLoading: isLoadingYTNCByHeartRisk,
    data: YTNCByHeartRisk,
    refetch: refetchYTNCByHeartRisk
  } = useQuery(
    ['getAllYTNCByHeartRiskV3'],
    () => getYTNCByHeartRiskV3Service({
      from: dateRange[0],
      to: dateRange[1],
      ...(typeParam && { type: typeParam }),
      ...(kiosGroupId.join(',') && { kioskGroupIds: kiosGroupId.join(',') }),
    }),
    {
      onError: (err: AxiosError) => {
        if (err.response?.status === 408 || err.response?.status === 504) {
          setIsErrorTimeout(true);
        }
      },
      onSuccess: () => {
        setIsErrorTimeout(false);
      }
    }
  );

  const {
    isLoading: isLoadingAtherosclerosis,
    data: atherosclerosis,
    refetch: refetchAtherosclerosis
  } = useQuery(
    ['getAllAtherosclerosisV3'],
    () => getAtherosclerosisV3Service({
      from: dateRange[0],
      to: dateRange[1],
      ...(typeParam && { type: typeParam }),
      ...(kiosGroupId.join(',') && { kioskGroupIds: kiosGroupId.join(',') }),
    }),
    {
      onError: (err: AxiosError) => {
        if (err.response?.status === 408 || err.response?.status === 504) {
          setIsErrorTimeout(true);
        }
      },
      onSuccess: () => {
        setIsErrorTimeout(false);
      }
    }
  );

  const {
    data: coutnerByTMXV,
    refetch: refetchCoutnerByTMXV
  } = useQuery(
    ['getCounterByTMXVV3'],
    () => getCounterByTMXVV3Service({
      from: dateRange[0],
      to: dateRange[1],
      ...(typeParam && { type: typeParam }),
      ...(kiosGroupId.join(',') && { kioskGroupIds: kiosGroupId.join(',') }),
    }),
    {
      onError: (err: AxiosError) => {
        if (err.response?.status === 408 || err.response?.status === 504) {
          setIsErrorTimeout(true);
        }
      },
      onSuccess: () => {
        setIsErrorTimeout(false);
      }
    }
  );

  const {
    isLoading: isLoadingYTNCForOneByHeartRisk,
    data: YTNCForOneByHeartRisk,
    refetch: refetchYTNCForOneByHeartRisk
  } = useQuery(
    ['getAllYTNCForOneByHeartRiskV3'],
    () => getYTNCForOneByHeartRiskV3Service({
      from: dateRange[0],
      to: dateRange[1],
      ...(typeParam && { type: typeParam }),
      ...(kiosGroupId.join(',') && { kioskGroupIds: kiosGroupId.join(',') }),
    }),
    {
      onError: (err: AxiosError) => {
        if (err.response?.status === 408 || err.response?.status === 504) {
          setIsErrorTimeout(true);
        }
      },
      onSuccess: () => {
        setIsErrorTimeout(false);
      }
    }
  );

  const handleClear = () => {
    setDateRange([]);
    setTypeParam(undefined);
    setKiosGroupId([]);
  };

  const transformedYTNCByHeartRiskData = useMemo(() => {
    if (YTNCByHeartRisk && ytncData) {
      const oneYTNCTotal = ytncData.oneYTNC;
      const twoYTNCTotal = ytncData.twoYTNC;
      const threeYTNCTotal = ytncData.threeYTNC;

      const oneYTNCVeryHighPercent = ((YTNCByHeartRisk.oneYTNC.find((it) => it.heartRiskCode === HeartRiskEnum.veryHigh)?.total || 0) * 100 / oneYTNCTotal).toFixed(0);
      const oneYTNCHighPercent = ((YTNCByHeartRisk.oneYTNC.find((it) => it.heartRiskCode === HeartRiskEnum.high)?.total || 0) * 100 / oneYTNCTotal).toFixed(0);
      const oneYTNCLowAndMedium = ((YTNCByHeartRisk.oneYTNC.find((it) => it.heartRiskCode === HeartRiskEnum.lowAndMedium)?.total || 0) * 100 / oneYTNCTotal).toFixed(0);

      const twoYTNCVeryHighPercent = ((YTNCByHeartRisk.twoYTNC.find((it) => it.heartRiskCode === HeartRiskEnum.veryHigh)?.total || 0) * 100 / twoYTNCTotal).toFixed(0);
      const twoYTNCHighPercent = ((YTNCByHeartRisk.twoYTNC.find((it) => it.heartRiskCode === HeartRiskEnum.high)?.total || 0) * 100 / twoYTNCTotal).toFixed(0);
      const twoYTNCLowAndMedium = ((YTNCByHeartRisk.twoYTNC.find((it) => it.heartRiskCode === HeartRiskEnum.lowAndMedium)?.total || 0) * 100 / twoYTNCTotal).toFixed(0);

      const threeYTNCVeryHighPercent = ((YTNCByHeartRisk.threeYTNC.find((it) => it.heartRiskCode === HeartRiskEnum.veryHigh)?.total || 0) * 100 / threeYTNCTotal).toFixed(0);
      const threeYTNCHighPercent = ((YTNCByHeartRisk.threeYTNC.find((it) => it.heartRiskCode === HeartRiskEnum.high)?.total || 0) * 100 / threeYTNCTotal).toFixed(0);
      const threeYTNCLowAndMedium = ((YTNCByHeartRisk.threeYTNC.find((it) => it.heartRiskCode === HeartRiskEnum.lowAndMedium)?.total || 0) * 100 / threeYTNCTotal).toFixed(0);

      return {
        labels: ['1 YTNC', '2 YTNC', '3 YTNC'],
        datasets: [
          {
            data: [
              oneYTNCVeryHighPercent,
              twoYTNCVeryHighPercent,
              threeYTNCVeryHighPercent
            ]
          },
          {
            data: [
              oneYTNCHighPercent,
              twoYTNCHighPercent,
              threeYTNCHighPercent
            ]
          },
          {
            data: [
              oneYTNCLowAndMedium,
              twoYTNCLowAndMedium,
              threeYTNCLowAndMedium
            ]
          }
        ]
      };
    }

    return undefined;
  }, [YTNCByHeartRisk, ytncData]);

  const { transformedAtherosclerosisData, atheRosclerosisCardItems, rawData } = useMemo(() => {
    let dataResult;
    const cardItemsResult: KioskCardStatisticsItem[] = [];

    // Hold the raw value
    let rawDataResult: number[] = [];
    if (atherosclerosis && kiosCounterData && coutnerByTMXV) {
      const totalYes = Object.values(atherosclerosis).reduce((a, b) => a + b.yes, 0);

      // Get data for chart
      rawDataResult = [
        (atherosclerosis.dotquyThieumau.yes),
        (atherosclerosis.nhoimaucotim.yes),
        (atherosclerosis.datstent.yes),
        (atherosclerosis.dauthatnguc.yes),
        (atherosclerosis.phinhtachdongmach.yes),
      ];
      dataResult = {
        datasets: [
          {
            data: [
              (atherosclerosis.dotquyThieumau.yes * 100 / totalYes).toFixed(0),
              (atherosclerosis.nhoimaucotim.yes * 100 / totalYes).toFixed(0),
              (atherosclerosis.datstent.yes * 100 / totalYes).toFixed(0),
              (atherosclerosis.dauthatnguc.yes * 100 / totalYes).toFixed(0),
              (atherosclerosis.phinhtachdongmach.yes * 100 / totalYes).toFixed(0),
            ],
          },
        ]
      };

      // Get data for card statistic
      cardItemsResult.push({
        title: t('dashboard2025.total'),
        value: kiosCounterData.total,
      });
      cardItemsResult.push({
        title: t('dashboard2025.TMXVIllness'),
        value: (coutnerByTMXV.total * 100 / kiosCounterData.total).toFixed(0),
        isPercent: true,
        subvalue: coutnerByTMXV.total,
      });
    }
    return {
      transformedAtherosclerosisData: dataResult,
      atheRosclerosisCardItems: cardItemsResult,
      rawData: rawDataResult,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [atherosclerosis, kiosCounterData, coutnerByTMXV]);

  const { transformedYTNCForOneByHeartRiskData, YTNCForOneByHeartRiskCardItems, YTNCForOneByHeartRiskRawData } = useMemo(() => {
    let dataResult;
    const cardItemsResult: KioskCardStatisticsItem[] = [];
    let rawDataResult: number[] = [];

    if (YTNCForOneByHeartRisk && kiosCounterData) {
      const ytncTHATotal = YTNCForOneByHeartRisk.ytncTHA.reduce((a, b) => a + b.total, 0);
      const ytncDTDTotal = YTNCForOneByHeartRisk.ytncDTD.reduce((a, b) => a + b.total, 0);
      const ytncRLLPTotal = YTNCForOneByHeartRisk.ytncRLLP.reduce((a, b) => a + b.total, 0);
      const ytncHTLTotal = YTNCForOneByHeartRisk.ytncHTL.reduce((a, b) => a + b.total, 0);

      // Get data for chart
      dataResult = {
        datasets: [
          {
            data: [
              ((YTNCForOneByHeartRisk.ytncTHA.find((it) => it.heartRiskCode === HeartRiskEnum.veryHigh)?.total || 0) * 100 / ytncTHATotal).toFixed(0),
              ((YTNCForOneByHeartRisk.ytncDTD.find((it) => it.heartRiskCode === HeartRiskEnum.veryHigh)?.total || 0) * 100 / ytncDTDTotal).toFixed(0),
              ((YTNCForOneByHeartRisk.ytncRLLP.find((it) => it.heartRiskCode === HeartRiskEnum.veryHigh)?.total || 0) * 100 / ytncRLLPTotal).toFixed(0),
              ((YTNCForOneByHeartRisk.ytncHTL.find((it) => it.heartRiskCode === HeartRiskEnum.veryHigh)?.total || 0) * 100 / ytncHTLTotal).toFixed(0),
            ]
          },
          {
            data: [
              ((YTNCForOneByHeartRisk.ytncTHA.find((it) => it.heartRiskCode === HeartRiskEnum.high)?.total || 0) * 100 / ytncTHATotal).toFixed(0),
              ((YTNCForOneByHeartRisk.ytncDTD.find((it) => it.heartRiskCode === HeartRiskEnum.high)?.total || 0) * 100 / ytncDTDTotal).toFixed(0),
              ((YTNCForOneByHeartRisk.ytncRLLP.find((it) => it.heartRiskCode === HeartRiskEnum.high)?.total || 0) * 100 / ytncRLLPTotal).toFixed(0),
              ((YTNCForOneByHeartRisk.ytncHTL.find((it) => it.heartRiskCode === HeartRiskEnum.high)?.total || 0) * 100 / ytncHTLTotal).toFixed(0),
            ]
          },
          {
            data: [
              ((YTNCForOneByHeartRisk.ytncTHA.find((it) => it.heartRiskCode === HeartRiskEnum.lowAndMedium)?.total || 0) * 100 / ytncTHATotal).toFixed(0),
              ((YTNCForOneByHeartRisk.ytncDTD.find((it) => it.heartRiskCode === HeartRiskEnum.lowAndMedium)?.total || 0) * 100 / ytncDTDTotal).toFixed(0),
              ((YTNCForOneByHeartRisk.ytncRLLP.find((it) => it.heartRiskCode === HeartRiskEnum.lowAndMedium)?.total || 0) * 100 / ytncRLLPTotal).toFixed(0),
              ((YTNCForOneByHeartRisk.ytncHTL.find((it) => it.heartRiskCode === HeartRiskEnum.lowAndMedium)?.total || 0) * 100 / ytncHTLTotal).toFixed(0),
            ]
          }
        ]
      };

      // Get data for card statistic
      cardItemsResult.push({
        title: t('dashboard2025.tha'),
        value: (ytncTHATotal * 100 / kiosCounterData.total).toFixed(0),
        isPercent: true,
        subvalue: ytncTHATotal
      });
      cardItemsResult.push({
        title: t('dashboard2025.dtd'),
        value: (ytncDTDTotal * 100 / kiosCounterData.total).toFixed(0),
        isPercent: true,
        subvalue: ytncDTDTotal
      });
      cardItemsResult.push({
        title: t('dashboard2025.rllp'),
        value: (ytncRLLPTotal * 100 / kiosCounterData.total).toFixed(0),
        isPercent: true,
        subvalue: ytncRLLPTotal
      });

      // Get raw data
      rawDataResult = [
        ytncTHATotal,
        ytncDTDTotal,
        ytncRLLPTotal,
        ytncHTLTotal
      ];
    }

    return {
      transformedYTNCForOneByHeartRiskData: dataResult,
      YTNCForOneByHeartRiskCardItems: cardItemsResult,
      YTNCForOneByHeartRiskRawData: rawDataResult
    };
  }, [YTNCForOneByHeartRisk, kiosCounterData, t]);

  return (
    <div className="riskHeartManagement">
      <HeaderKiosk
        icon={<Icon iconName="heartbeat2" />}
        title={t('dashboard2025.heartRisk2025')}
      />
      <div className="riskHeartManagement-wrapper">
        <KioskDashboardFilter
          typeParam={typeParam}
          setTypeParam={setTypeParam}
          kiosGroupId={kiosGroupId}
          setKiosGroupId={setKiosGroupId}
          dateRange={dateRange}
          setDateRange={setDateRange}
          onClear={handleClear}
          onRefetch={refetchCounter}
          onRefetch2={refetchHeartRiskCounter}
          onRefetch3={refetch}
          onRefetch4={refetchYTNCByHeartRisk}
          onRefetch5={() => {
            refetchCoutnerByTMXV();
            refetchAtherosclerosis();
          }}
          onRefetch6={refetchYTNCForOneByHeartRisk}
        />
        <div className="riskHeartManagement-card">
          <SummaryCard
            variant="tim-mach"
            title={t('dashboard2025.total')}
            number={kiosCounterData?.total || 0}
          />
          <SummaryCard
            variant="tim-mach"
            title={t('dashboard2025.riskTotal')}
            number={counterHeartRiskData?.total || 0}
          />
        </div>
        <Spin spinning={isFetching || isLoadingYTNCByHeartRisk || isLoadingAtherosclerosis || isLoadingYTNCForOneByHeartRisk}>
          <div className="riskHeartManagement-charts">
            {
              isErrorTimeout ? (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'start',
                    height: '100%',
                    width: '100%',
                  }}
                >
                  <Typography.Title level={4}>
                    {t('message.errorTimeout')}
                  </Typography.Title>
                </div>
              ) : (
                <>
                  {getPermission(rolesUser, roles.REPORT_YTNC) && (
                    <CardiovascularRisk
                      cardiovascularRisk={ytncData}
                    />
                  )}
                  {getPermission(rolesUser, roles.REPORT_YTNC_BY_HEART_RISK) && (
                    <KioskCardiovascularRiskChart
                      title={t('dashboard2025.YTNCByHeartRisk')}
                      data={transformedYTNCByHeartRiskData}
                      style={{ minHeight: 600 }}
                    />
                  )}
                  {getPermission(rolesUser, roles.REPORT_ATHEROSCLEROSIS) && (
                    <KioskAtherosclerosisChart
                      data={transformedAtherosclerosisData}
                      dataOnTop={rawData}
                      cardItems={atheRosclerosisCardItems}
                      style={{ minHeight: 700 }}
                    />
                  )}
                  {getPermission(rolesUser, roles.REPORT_YTNC_FOR_ONE_BY_HEART_RISK) && (
                    <KioskHeartRiskClassify
                      title={t('dashboard2025.YTNCForOneByHeartRisk')}
                      data={transformedYTNCForOneByHeartRiskData}
                      style={{ minHeight: 700 }}
                      cardItems={YTNCForOneByHeartRiskCardItems}
                      dataOnTop={YTNCForOneByHeartRiskRawData}
                    />
                  )}
                </>
              )
            }
          </div>
        </Spin>
      </div>
    </div>
  );
};

export default RiskHeartManagement;
